import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../lib/api';
import {
  buildFinishDecisions, finishChoicesComplete, finishErrorMessage, initialFinishChoices, isPublishingState,
} from './inlineEdit';

// Finish a stuck website update from the admin record:
//   GET  /api/admin/edit-requests/:id/finish  → { canFinish, blockedReason, stuck: [...] }
//   POST /api/admin/edit-requests/:id/finish  { decisions } → { editRequest, dispatched }
// then poll GET /api/admin/edit-requests/:id every 3 s while it publishes,
// until it is live or stops again (needs_review / failed / deploy_failed —
// then the stuck list is fetched again with the new results).
//
//   phase: 'off'          not used (not admin, or the update doesn't need a person)
//          'loading'      asking what is stuck
//          'ready'        stuck list + choices; submit() publishes
//          'blocked'      can't finish from here (blockedReason)
//          'unavailable'  the question itself failed (reload() asks again)
//          'sending'      the POST is on its way
//          'publishing'   following the publish
//          'live'         the publish went live
//          'lost'         polling lost contact with the portal
//   error:  plain words for a refused POST or lost contact ('' when none)
//   notice: plain words after a publish that stopped again ('' when none)
//   onUpdate(er) receives every newer version of the record (POST + polls).

export const FINISH_POLL_MS = 3000;
const NEEDS_PERSON = ['needs_review', 'failed', 'deploy_failed'];
const MAX_MISSES = 5;

export default function useInlineFinish(requestId, { active = false, onUpdate, pollMs = FINISH_POLL_MS } = {}) {
  const [phase, setPhase] = useState(active && requestId ? 'loading' : 'off');
  const [stuck, setStuck] = useState([]);
  const [blockedReason, setBlockedReason] = useState(null);
  const [choices, setChoices] = useState({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const idRef = useRef(requestId);
  idRef.current = requestId;
  const sendingRef = useRef(false);

  const load = useCallback(async () => {
    const id = requestId;
    if (!id) return;
    setPhase('loading');
    setBlockedReason(null);
    try {
      const data = await api.get(`/api/admin/edit-requests/${id}/finish`);
      if (idRef.current !== id) return;
      if (data?.canFinish) {
        const list = Array.isArray(data.stuck) ? data.stuck : [];
        setStuck(list);
        setChoices(initialFinishChoices(list));
        setPhase('ready');
      } else {
        setStuck([]);
        setBlockedReason(data?.blockedReason || 'not_stuck');
        setPhase('blocked');
      }
    } catch {
      if (idRef.current === id) setPhase('unavailable');
    }
  }, [requestId]);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // Ask when the record opens (and again for another request). `active`
  // turning off while this hook follows its own publish changes nothing.
  const lastIdRef = useRef(requestId);
  useEffect(() => {
    const sameRequest = lastIdRef.current === requestId;
    lastIdRef.current = requestId;
    const following = ['sending', 'publishing', 'live', 'lost'].includes(phaseRef.current);
    if (!active && following && sameRequest) return;
    setStuck([]);
    setChoices({});
    setError('');
    setNotice('');
    if (active && requestId) load();
    else setPhase('off');
  }, [requestId, active, load]);

  const choose = useCallback((changeId, patch) => {
    setChoices((prev) => ({ ...prev, [changeId]: { ...prev[changeId], ...patch } }));
  }, []);

  const complete = phase === 'ready' && finishChoicesComplete(stuck, choices);

  const submit = useCallback(async () => {
    if (!requestId || phase !== 'ready' || sendingRef.current || !finishChoicesComplete(stuck, choices)) return;
    const id = requestId;
    sendingRef.current = true;
    setError('');
    setNotice('');
    setPhase('sending');
    try {
      const data = await api.post(`/api/admin/edit-requests/${id}/finish`, { decisions: buildFinishDecisions(stuck, choices) });
      if (idRef.current !== id) return;
      const er = data?.editRequest || {};
      onUpdateRef.current?.({ ...er, publishState: isPublishingState(er.publishState) ? er.publishState : 'publishing' });
      setPhase('publishing');
    } catch (err) {
      if (idRef.current !== id) return;
      setError(finishErrorMessage(err));
      setPhase('ready');
    } finally {
      sendingRef.current = false;
    }
  }, [requestId, phase, stuck, choices]);

  useEffect(() => {
    if (phase !== 'publishing' || !requestId) return undefined;
    const id = requestId;
    let misses = 0;
    let stopped = false;
    const t = setInterval(async () => {
      let er = null;
      try {
        const data = await api.get(`/api/admin/edit-requests/${id}`);
        er = data?.editRequest || null;
      } catch {
        er = null;
      }
      if (stopped || idRef.current !== id) return;
      // A record without its publish state can't tell us where it stands.
      if (!er || typeof er.publishState !== 'string') {
        misses += 1;
        if (misses >= MAX_MISSES) {
          stopped = true;
          setError('We lost contact with the portal. Check your connection, then reopen this record in a few minutes.');
          setPhase('lost');
        }
        return;
      }
      misses = 0;
      onUpdateRef.current?.(er);
      if (isPublishingState(er.publishState)) return;
      stopped = true;
      if (er.publishState === 'live') {
        setPhase('live');
      } else if (NEEDS_PERSON.includes(er.publishState)) {
        setNotice('Publishing stopped again. What still needs a person is shown on the changes above.');
        load();
      } else {
        setPhase('off');
      }
    }, pollMs);
    return () => { stopped = true; clearInterval(t); };
  }, [phase, requestId, pollMs, load]);

  return { phase, stuck, blockedReason, choices, complete, error, notice, choose, submit, reload: load };
}
